/**
 * Refusals worth telling a person about, one record per account.
 *
 * There used to be a single slot, `chat:impostor`, holding the last refusal.
 * Two things followed from that, and both were found on a real phone:
 *
 *  1. Clearing it wrote `{...null, seenAt}` — an object with a timestamp and
 *     nothing else. Truthy. So the next launch read it back and showed the
 *     warning again, now with no account, no device and no reason: the screen
 *     said «сменился ключ собеседника (id undefined)». Accepting the key again
 *     wrote the same husk again, so it could not be got rid of at all. That is
 *     the empty red rectangle on the iPhone, dated 03.09.2026 12:40 — the
 *     moment the key WAS accepted.
 *  2. One slot cannot hold two facts. With two contacts in trouble the second
 *     refusal erased the first; with a hundred, a person sees one banner about
 *     whoever was last, and every other refusal is silently dropped.
 *
 * So: one record per account, addressed by account id, each carrying what
 * happened, which device, and when. A record without an account id is not a
 * warning — it is a bug, and it is dropped on sight rather than shown.
 *
 * Nothing here decides anything. Refusing a message is chatTrust and the
 * device-list gate; this module only remembers what they refused, so the
 * screen can say it and a person can answer it.
 */

export const NOTICE_PREFIX = 'chat:impostor:';
const LEGACY_KEY = 'chat:impostor';

export function noticeKey(userId) {
  return NOTICE_PREFIX + String(userId);
}

/** A record is only a warning if it says whose account it is about. */
export function isUsable(rec) {
  return !!rec && rec.userId != null && Number.isFinite(Number(rec.userId));
}

/**
 * Remember one refusal. Returns the stored record, or null when the caller
 * could not say who it was about — in which case nothing is stored.
 */
export async function recordNotice(store, notice) {
  if (!isUsable(notice)) return null;
  const rec = {
    userId: Number(notice.userId),
    deviceId: notice.deviceId || null,
    reason: notice.reason || '',
    masterChanged: !!notice.masterChanged,
    at: notice.at || new Date().toISOString(),
  };
  await store.setValue(noticeKey(rec.userId), rec);
  return rec;
}

/** The person answered for this account: the record goes, not its contents. */
export async function clearNotice(store, userId) {
  await store.deleteValue(noticeKey(userId));
}

/**
 * Every open refusal, newest first — and the old single slot cleaned up on
 * the way past: converted when it names an account, deleted when it is the
 * husk described above.
 */
export async function loadNotices(store) {
  const legacy = await store.getValue(LEGACY_KEY);
  if (legacy) {
    if (isUsable(legacy)) await recordNotice(store, { ...legacy, at: legacy.at || legacy.seenAt });
    await store.deleteValue(LEGACY_KEY);
  }
  const keys = await store.valueKeys(NOTICE_PREFIX);
  const out = [];
  for (const k of keys) {
    const rec = await store.getValue(k);
    if (isUsable(rec)) out.push(rec);
    else await store.deleteValue(k);
  }
  return out.sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
}
